import { useState, useEffect, useMemo, useRef } from 'react';
import { useAtom } from 'jotai';

import { authenticationAtom } from '../../State';
import { storageAPI } from '../../StorageAPI';
import Tree, { TreeNode } from '../rag/Tree/Tree';
import { ManagedFolderListResult } from '@the-real-insight/tri-model';
import {
    applySelectedPaths,
    folderPathToRequestPath,
    isUnderFolder,
    replaceFolderChildren,
    collectLoadedFolderPaths,
} from './fileTreePickerUtils';

import { Skeleton } from '@/components/ui/skeleton';

import VolumeSelector, { useVolumes } from '../../components/VolumeSelector';

/**
 * A file the user checked. Carries what registration needs for a file that has no `_id` yet —
 * including `volumeKey`, because a selection may span volumes (check a file, switch volume, check
 * another) and registering it against the wrong one would store the wrong `volume` on the record.
 */
export interface PickedFile {
    _id?: string;
    name: string;
    path: string;
    providerId?: string;
    webUrl?: string;
    volumeKey?: string;
}

/** A folder the user checked. Its contents are resolved server-side, not here. */
export interface PickedFolder {
    name: string;
    path: string;
    volumeKey?: string;
}

export interface PickerSelection {
    files: PickedFile[];
    folders: PickedFolder[];
}

export const emptyPickerSelection: PickerSelection = { files: [], folders: [] };

interface FileTreePickerProps {
    onFileSelect?: (id: string, name: string) => void;
    fileTypeFilter?: string; // e.g., 'markdown', 'image', 'pdf', etc.
    /**
     * Opt-in multi-select. When a selection is supplied the picker renders checkboxes on files
     * (and, with `selectFolders`, on folders) and reports what is checked instead of resolving a
     * click to a single file id — so a selection can be reviewed before it is acted on. Without it
     * the picker keeps its click-to-select behaviour.
     */
    selection?: PickerSelection;
    onSelectionChange?: (selection: PickerSelection) => void;
    /** Allow whole folders to be checked (multi-select only). */
    selectFolders?: boolean;
}

export default function FileTreePicker({
    onFileSelect,
    fileTypeFilter,
    selection,
    onSelectionChange,
    selectFolders,
}: FileTreePickerProps) {
    const [authentication] = useAtom(authenticationAtom) as any;
    // Which managed-file volume is being browsed. A picked file is referenced by its `_id`, so
    // everything downstream of the picker is volume-agnostic.
    const {
        volumes,
        activeVolumeKey: storageVolume,
        selectVolume,
    } = useVolumes(authentication?.customer);
    const [data, setData] = useState<TreeNode[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [folderStates, setFolderStates] = useState<Map<string, boolean>>(new Map());
    // Folders whose children have been resolved (either populated by the initial
    // listing — the S3 case — or already lazily fetched). Used to avoid redundant
    // fetches when a folder is expanded. See fileTreePickerUtils for the rationale.
    const loadedFoldersRef = useRef<Set<string>>(new Set());
    // Folders with an in-flight lazy fetch, to dedupe rapid expand/collapse toggles.
    const loadingFoldersRef = useRef<Set<string>>(new Set());
    // Files with an in-flight registration. The underlying Tree emits its click
    // twice per click, so this dedupes the second one into a no-op.
    const registeringPathsRef = useRef<Set<string>>(new Set());
    // Numbers each listing request so only the latest one may touch state. Switching volume while a
    // listing is in flight otherwise lets the older response land last — a broken volume's late
    // error would stay on screen over the tree of the volume switched to.
    const loadRequestRef = useRef(0);

    const multiSelect = Boolean(selection && onSelectionChange);
    // The node handlers are created once per tree build but must toggle against the *current*
    // selection, which the parent owns — so they read it from a ref rather than a stale closure.
    const selectionRef = useRef<PickerSelection>(selection ?? emptyPickerSelection);
    selectionRef.current = selection ?? emptyPickerSelection;

    // A path only identifies a pick *within* one volume — the same path can exist in two of them —
    // so every comparison is (volume, path). Read through a ref for the same reason the selection is:
    // the node handlers are built once per tree and must see the current volume, not a stale one.
    const volumeRef = useRef<string>(storageVolume);
    volumeRef.current = storageVolume;

    const isSamePick = (pick: { path: string; volumeKey?: string }, path: string) =>
        pick.path === path && (pick.volumeKey ?? volumeRef.current) === volumeRef.current;

    const toggleFile = (item: any, checked: boolean) => {
        const current = selectionRef.current;
        const files = checked
            ? current.files.some(file => isSamePick(file, item.path))
                ? current.files
                : [
                      ...current.files,
                      {
                          _id: item._id ? String(item._id) : undefined,
                          name: item.name,
                          path: item.path,
                          providerId: item.providerId,
                          webUrl: item.webUrl,
                          volumeKey: volumeRef.current,
                      },
                  ]
            : current.files.filter(file => !isSamePick(file, item.path));
        onSelectionChange?.({ ...current, files });
    };

    const toggleFolder = (path: string, name: string, checked: boolean) => {
        const current = selectionRef.current;
        if (!checked) {
            onSelectionChange?.({
                ...current,
                folders: current.folders.filter(folder => !isSamePick(folder, path)),
            });
            return;
        }
        if (current.folders.some(folder => isSamePick(folder, path))) {
            return;
        }
        // A picked folder means its whole subtree, so anything already checked inside it is now
        // covered by the folder. Dropping those keeps one file from travelling twice — once as an
        // id, once out of the server's folder walk — which would also charge it twice against the
        // request's 50-file ceiling and refuse a selection that is in fact within it.
        const coveredByThisFolder = (pick: PickedFile | PickedFolder) =>
            (pick.volumeKey ?? volumeRef.current) === volumeRef.current &&
            isUnderFolder(path, pick.path);
        onSelectionChange?.({
            files: current.files.filter(file => !coveredByThisFolder(file)),
            folders: [
                ...current.folders.filter(folder => !coveredByThisFolder(folder)),
                { name, path, volumeKey: volumeRef.current },
            ],
        });
    };

    // Function to update folder state when user toggles it
    const updateFolderState = (path: string, isOpen: boolean) => {
        setFolderStates(prev => {
            const newStates = new Map(prev);
            newStates.set(path, isOpen);
            return newStates;
        });
    };

    // Check if file matches the filter
    const fileMatchesFilter = (fileName: string, filterType?: string): boolean => {
        if (!filterType || filterType === 'any') return true;
        
        const ext = fileName.split('.').pop()?.toLowerCase() || '';
        
        switch (filterType) {
            case 'markdown':
                return ext === 'md' || ext === 'markdown';
            case 'text':
                return ['txt', 'text', 'md', 'markdown', 'csv', 'json', 'xml', 'yaml', 'yml'].includes(ext);
            case 'image':
                return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext);
            case 'pdf':
                return ext === 'pdf';
            case 'excel':
                return ['xlsx', 'xls', 'csv'].includes(ext);
            case 'word':
                return ['docx', 'doc'].includes(ext);
            case 'powerpoint':
                return ['pptx', 'ppt'].includes(ext);
            default:
                return true;
        }
    };

    // Create a read-only file node (no edit/delete/rename handlers)
    const createFileNode = (item: any): TreeNode => {
        if (multiSelect) {
            return {
                type: 'file' as const,
                name: item.name,
                path: item.path,
                selected: false,
                processing: false,
                showCheckbox: true,
                onSelect: (checked: boolean) => toggleFile(item, checked),
            };
        }
        return {
            type: 'file' as const,
            name: item.name,
            path: item.path,
            selected: false,
            processing: false,
            showCheckbox: false,
            // File click handler for selection - passes the ManagedFile _id.
            onClick: async () => {
                if (!onFileSelect) return;
                let id = item._id;
                // SharePoint files created outside the platform have no ManagedFile
                // document (hence no _id). Materialize one on selection so the file
                // can be referenced. Forward the metadata we already have from the
                // listing so the server skips a slow storage lookup, and dedupe the
                // duplicate click event the Tree emits.
                if (!id) {
                    if (registeringPathsRef.current.has(item.path)) return;
                    registeringPathsRef.current.add(item.path);
                    try {
                        const registered = await storageAPI.registerFile(
                            authentication.customer,
                            storageVolume,
                            item.path,
                            { providerId: item.providerId, name: item.name, webUrl: item.webUrl }
                        );
                        id = registered?._id;
                    } catch (err) {
                        console.error(`Failed to register file "${item.path}":`, err);
                        return;
                    } finally {
                        registeringPathsRef.current.delete(item.path);
                    }
                }
                if (id) {
                    onFileSelect(id, item.name);
                }
            }
            // Note: No onDelete, onRename, onDownload - read-only mode
        };
    };

    // Create a read-only folder node (no create/delete/rename handlers)
    const createFolderNode = (path: string, name: string, files: TreeNode[] = []): TreeNode => {
        return {
            type: 'folder',
            name: name,
            path: path,
            isOpen: folderStates.get(path) ?? false,
            files: files,
            isUploading: false,
            selected: false,
            showCheckbox: Boolean(multiSelect && selectFolders),
            onSelect: (checked: boolean) => toggleFolder(path, name, checked),
            onToggleOpen: (isOpen: boolean) => {
                updateFolderState(path, isOpen);
                // Lazily fetch this folder's children on first expand. Needed for
                // providers (SharePoint) whose listing is single-level, so subfolders
                // arrive empty; a no-op for folders already populated (S3).
                if (isOpen) {
                    void loadFolderChildren(path);
                }
            }
            // Note: No onDelete, onCreateFolder, onRename, onFileDrop, onFileMove - read-only mode
        };
    };

    // Apply preserved folder states to tree data
    const applyFolderStates = (nodes: TreeNode[], preservedStates: Map<string, boolean>): TreeNode[] => {
        return nodes.map(node => {
            if (node.type === 'folder' && node.path) {
                const preservedIsOpen = preservedStates.get(node.path);
                const isOpen = preservedIsOpen ?? node.isOpen ?? false;
                
                const updatedNode = { ...node, isOpen };
                
                if (node.files) {
                    updatedNode.files = applyFolderStates(node.files, preservedStates);
                }
                
                return updatedNode;
            }
            return node;
        });
    };

    // Convert storage data to tree nodes
    const toTreeNodes = (folderContents: ManagedFolderListResult): TreeNode[] => {
        const folderMap = new Map<string, TreeNode>();
        const rootFiles: TreeNode[] = [];

        // First pass: create all directories
        folderContents.items.forEach((item: any) => {
            if (item.isDirectory) {
                const pathParts = item.path.split('/').filter((part: string) => part !== '');
                const folderName = pathParts[pathParts.length - 1] || 'Unknown';
                
                folderMap.set(item.path, createFolderNode(item.path, folderName));
            }
        });

        // Second pass: organize files into their parent directories
        folderContents.items.forEach((item: any) => {
            if (!item.isDirectory) {
                // Apply file type filter
                if (!fileMatchesFilter(item.name, fileTypeFilter)) {
                    return; // Skip files that don't match the filter
                }
                
                const fileNode = createFileNode(item);
                
                // Determine parent directory path
                const pathParts = item.path.split('/').filter((part: string) => part !== '');
                const parentPath = pathParts.length > 1 ? '/' + pathParts.slice(0, -1).join('/') + '/' : '/';
                
                if (parentPath === '/' || !folderMap.has(parentPath)) {
                    rootFiles.push(fileNode);
                } else {
                    const parentFolder = folderMap.get(parentPath);
                    if (parentFolder && parentFolder.files) {
                        parentFolder.files.push(fileNode);
                    } else {
                        rootFiles.push(fileNode);
                    }
                }
            }
        });

        // Third pass: organize folders into their parent directories
        const rootFolders: TreeNode[] = [];

        folderMap.forEach((folder, path) => {
            const pathParts = path.split('/').filter((part: string) => part !== '');
            const parentPath = pathParts.length > 1 ? '/' + pathParts.slice(0, -1).join('/') + '/' : '/';
            
            if (parentPath === '/' || !folderMap.has(parentPath)) {
                rootFolders.push(folder);
            } else {
                const parentFolder = folderMap.get(parentPath);
                if (parentFolder && parentFolder.files) {
                    parentFolder.files.push(folder);
                } else {
                    rootFolders.push(folder);
                }
            }
        });

        // Build the final tree structure
        const treeNodes: TreeNode[] = [...rootFiles, ...rootFolders];

        return treeNodes;
    };

    // Convert a single folder's listing into child tree nodes (files first, then
    // folders — matching toTreeNodes' root ordering), applying the file filter.
    const itemsToNodes = (items: any[]): TreeNode[] => {
        const folders: TreeNode[] = [];
        const files: TreeNode[] = [];
        items.forEach((item: any) => {
            if (item.isDirectory) {
                const pathParts = item.path.split('/').filter((part: string) => part !== '');
                const folderName = item.name || pathParts[pathParts.length - 1] || 'Unknown';
                folders.push(createFolderNode(item.path, folderName));
            } else if (fileMatchesFilter(item.name, fileTypeFilter)) {
                files.push(createFileNode(item));
            }
        });
        return [...files, ...folders];
    };

    // Fetch a folder's immediate children on demand and splice them into the tree.
    const loadFolderChildren = async (path: string) => {
        if (!authentication?.customer) return;
        if (loadedFoldersRef.current.has(path) || loadingFoldersRef.current.has(path)) {
            return;
        }

        loadingFoldersRef.current.add(path);
        try {
            const requestPath = folderPathToRequestPath(path);
            const result = await storageAPI.getFileOrListFolder(
                authentication.customer,
                storageVolume,
                requestPath
            );
            const items = (result as ManagedFolderListResult)?.items ?? [];
            const childNodes = itemsToNodes(items);
            loadedFoldersRef.current.add(path);
            setData(prev => replaceFolderChildren(prev, path, childNodes));
        } catch (err: any) {
            // Leave the folder unmarked so a later expand can retry.
            console.error(`Failed to load folder "${path}":`, err);
        } finally {
            loadingFoldersRef.current.delete(path);
        }
    };

    // Load storage data
    const loadStorage = async () => {
        const request = ++loadRequestRef.current;
        const isStale = () => request !== loadRequestRef.current;

        if (!authentication?.customer) {
            setError('No authentication available');
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            // Reset lazy-load bookkeeping for the fresh listing.
            loadingFoldersRef.current = new Set();

            const storage = await storageAPI.getStorage(authentication.customer, storageVolume);
            if (isStale()) return;
            const treeNodes = toTreeNodes(storage);
            const treeNodesWithPreservedStates = applyFolderStates(treeNodes, folderStates);

            // Seed folders that already arrived with children (S3 returns the whole
            // subtree up front) so expanding them doesn't trigger a redundant fetch.
            loadedFoldersRef.current = collectLoadedFolderPaths(treeNodesWithPreservedStates);

            setData(treeNodesWithPreservedStates);
        } catch (err: any) {
            if (isStale()) return;
            console.error('Failed to load storage:', err);
            setError(err.message || 'Failed to load files');
        } finally {
            if (!isStale()) setIsLoading(false);
        }
    };

    useEffect(() => {
        loadStorage();
    }, [authentication?.customer, fileTypeFilter, storageVolume]);

    // Selection is held by path outside the tree (the tree is rebuilt as folders lazily load), so
    // stamp it back on for rendering. Unchanged nodes keep their object identity, so checking a box
    // does not remount — and therefore does not collapse — the folders around it.
    const treeData = useMemo(() => {
        if (!multiSelect) {
            return data;
        }
        // Only picks made in the volume currently on screen get a checkmark — a path selected in
        // another volume names a different file here, if it exists at all.
        const inActiveVolume = (pick: { volumeKey?: string }) =>
            (pick.volumeKey ?? storageVolume) === storageVolume;
        const selectedPaths = new Set([
            ...(selection?.files || []).filter(inActiveVolume).map(file => file.path),
            ...(selection?.folders || []).filter(inActiveVolume).map(folder => folder.path),
        ]);
        return applySelectedPaths(data, selectedPaths);
    }, [data, multiSelect, selection, storageVolume]);

    // The tree body, which may be a spinner, an error or an empty state. The volume selector is
    // rendered above all of them: an empty or failing volume is exactly when the user needs to be
    // able to switch away from it.
    const body = (() => {
        if (isLoading) {
            return (
                // Folder and file rows as a placeholder, indented like the real tree.
                <div className="flex h-[200px] flex-col gap-2.5 overflow-hidden p-3" role="status" aria-busy="true" aria-label="Loading files">
                    {[0, 1, 1, 2, 2, 0, 1].map((depth, index) => (
                        <div
                            key={index}
                            className="flex items-center gap-2 animate-in fade-in-0 fill-mode-both duration-300"
                            style={{ paddingLeft: depth * 18, animationDelay: `${index * 45}ms` }}
                        >
                            <Skeleton className="size-4 shrink-0 rounded" />
                            <Skeleton className="h-3.5" style={{ width: `${[46, 62, 38, 70, 54, 42, 58][index]}%` }} />
                        </div>
                    ))}
                </div>
            );
        }

        if (error) {
            return (
                <div className="p-4 text-center text-xs text-destructive">
                    {error}
                </div>
            );
        }

        if (data.length === 0) {
            return (
                <div className="p-4 text-center text-xs text-muted-foreground">
                    No files available
                </div>
            );
        }

        return (
            <div className="max-h-[400px] overflow-auto rounded-md border border-input bg-background">
                <Tree
                    data={treeData}
                    onUpdate={() => {}}
                    onNodeClick={() => {}}
                    // No onFileDrop, onFileMove, onCreateRootFolder - read-only mode
                />
            </div>
        );
    })();

    return (
        <>
            <VolumeSelector
                volumes={volumes}
                activeVolumeKey={storageVolume}
                onSelect={selectVolume}
            />
            {body}
        </>
    );
}

